import React, { useState, useEffect, useMemo, useRef } from 'react';
import './Schedule.css';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Initialize the localizer for Big Calendar
const localizer = momentLocalizer(moment);

const Schedule = ({ courses, removeCourse, hoveredCourse, addCourse, onDownloadSchedule, onUploadSchedule, onClearSchedule }) => {
  const [view, setView] = useState('calendar'); // calendar or list
  const [debug, setDebug] = useState(false); // For toggling debug info
  const [showDebug, setShowDebug] = useState(false); // For toggling debug info
  const [contextMenu, setContextMenu] = useState(null); // For right-click menu
  const [compactView, setCompactView] = useState(false); // For toggling compact view
  const [dropdownOpen, setDropdownOpen] = useState(false); // For export dropdown menu
  const [showGoogleModal, setShowGoogleModal] = useState(false); // For Google Calendar instructions modal
  const [excludeBreaks, setExcludeBreaks] = useState(true); // For academic calendar dates with no classes
  const [showImportModal, setShowImportModal] = useState(false); // For import modal
  const [importStatus, setImportStatus] = useState(''); // For import status messages
  const importInputRef = useRef(null); // Ref for file input
  
  const dayNames = {
    'M': 'Monday',
    'T': 'Tuesday',
    'W': 'Wednesday',
    'R': 'Thursday',
    'F': 'Friday'
  };
  
  // For debugging - verify course data is being received
  useEffect(() => {
    console.log("Current courses in Schedule:", courses);
    
    // Print full details of courses for debugging
    if (courses && courses.length > 0) {
      console.log("Course sample details:");
      courses.forEach((course, i) => {
        console.log(`Course ${i+1}:`, {
          CRN: course.CRN,
          Course: course.Course,
          Title: course.Title,
          Days: course.Days,
          Times: course.Times,
          DaysType: typeof course.Days,
          TimesType: typeof course.Times
        });
      });
    }
  }, [courses]);

  // Helper function to parse time strings in various formats
  const parseTimeString = (dateMoment, timeStr) => {
    if (!timeStr) return null;
    
    try {
      console.log(`Parsing time string: "${timeStr}"`);
      
      // Fix: Log the raw time string to diagnose the issue
      console.log(`Raw time string type: ${typeof timeStr}, value: "${timeStr}"`);
      
      let hours, minutes;
      let isPM = timeStr.toUpperCase().includes('PM');
      let isAM = timeStr.toUpperCase().includes('AM');
      
      // Try to extract time from Python-like tuple format first
      const pythonTupleMatch = timeStr.match(/\('([^']+)', '([^']+)'\)/);
      if (pythonTupleMatch && pythonTupleMatch.length >= 3) {
        // If it matches, just parse the first part of the tuple
        console.log("Found Python tuple format, parsing first part:", pythonTupleMatch[1]);
        return parseTimeString(dateMoment, pythonTupleMatch[1]);
      }
      
      // Remove AM/PM for parsing
      let timeOnly = timeStr.replace(/\s*(AM|PM)/i, '');
      
      // Handle direct hour-minute format like "13:25" (24-hour)
      if (timeOnly.includes(':')) {
        const [hoursStr, minutesStr] = timeOnly.split(':');
        hours = parseInt(hoursStr);
        minutes = parseInt(minutesStr);
        console.log(`Parsed time with colon: ${hours}:${minutes}`);
      }
      // Check if military time format (4 digits without colon)
      else if (/^\d{4}$/.test(timeOnly)) {
        hours = parseInt(timeOnly.substring(0, 2));
        minutes = parseInt(timeOnly.substring(2, 4));
        console.log(`Parsed military time: ${hours}:${minutes}`);
      }
      // Handle format like "8" or "14"
      else {
        hours = parseInt(timeOnly);
        minutes = 0;
        console.log(`Parsed simple hour: ${hours}:${minutes}`);
      }
      
      // Ensure hours and minutes are valid numbers
      if (isNaN(hours) || isNaN(minutes)) {
        console.error(`Invalid hours or minutes: ${hours}:${minutes}`);
        return null;
      }
      
      // For 24-hour format (like "13:25"), keep as is
      if (hours >= 13 && hours <= 23) {
        console.log(`Using 24-hour time as is: ${hours}:${minutes}`);
        // No adjustment needed
      }
      // Adjust for AM/PM if specified
      else if (isPM && hours < 12) {
        hours += 12;
        console.log(`Adjusted for PM: ${hours}:${minutes}`);
      } else if (isAM && hours === 12) {
        hours = 0;
        console.log(`Adjusted for 12AM: ${hours}:${minutes}`);
      }
      // Make assumptions for ambiguous times
      else if (!isAM && !isPM) {
        // Handle morning times (assumed AM)
        if (hours >= 7 && hours <= 12) {
          console.log(`Assumed morning class (AM): ${hours}:${minutes}`);
          // No adjustment needed for AM
        }
        // Handle early hours without AM/PM (assume PM)
        else if (hours < 7) {
          hours += 12;
          console.log(`Assumed afternoon class (PM) for hour < 7: ${hours}:${minutes}`);
        }
      }
      
      // Create the moment date object
      try {
        // Clone the date to avoid mutations
        const result = dateMoment.clone().hours(hours).minutes(minutes).seconds(0);
        console.log(`Final time result: ${result.format('ddd h:mm A')}`);
        return result;
      } catch (e) {
        console.error(`Error creating moment date: ${e.message}`);
        return null;
      }
    } catch (e) {
      console.error("Error parsing time string:", timeStr, e);
      return null;
    }
  };

  // Function to create events for a given course
  const createEventsForCourse = (course, isPreview = false) => {
    console.log(`Creating events for course: ${course.Course}${isPreview ? ' (preview)' : ''}`);
    const events = [];
    
    try {
      // SPECIAL CASE: Handle the exact format in the debug (MR,F with nested time tuples)
      // Handle the format with day groups like ["MR","F"] and time arrays like ["[('13:25', '14:40'), ('10:00', '10:50')]"]
      if (Array.isArray(course.Days) && 
          Array.isArray(course.Times) && 
          course.Times.length === 1 && 
          typeof course.Times[0] === 'string' && 
          course.Times[0].includes("('") && 
          course.Times[0].includes("')")) {
        
        console.log("Processing special case format with day groups and time tuples");
        
        // Extract all time pairs from the Python-like string
        const timesString = course.Times[0];
        // Match all tuples in format ('HH:MM', 'HH:MM')
        const timeMatches = timesString.match(/\('([^']+)', '([^']+)'\)/g);
        
        if (timeMatches && timeMatches.length > 0) {
          const extractedTimes = [];
          
          // Extract each time pair
          timeMatches.forEach(match => {
            const pairMatch = match.match(/\('([^']+)', '([^']+)'\)/);
            if (pairMatch && pairMatch.length >= 3) {
              extractedTimes.push({
                start: pairMatch[1],
                end: pairMatch[2]
              });
            }
          });
          
          console.log("Extracted time pairs:", extractedTimes);
          
          // Get buildings (locations) as array for matching with times
          let locations = [];
          if (typeof course.Location === 'string') {
            locations = [course.Location];
          } else if (Array.isArray(course.Location)) {
            locations = course.Location;
          }
          
          // Associate each day group with the corresponding time pair
          course.Days.forEach((dayGroup, index) => {
            // Use the matching time pair if available, or use the first pair as fallback
            const timePair = index < extractedTimes.length ? 
                             extractedTimes[index] : 
                             extractedTimes[0];
            
            // Get corresponding location for this time pair
            const location = index < locations.length ? locations[index] : (locations[0] || 'TBA');
            
            // Process each character in the day group (e.g., "MR" -> 'M', 'R')
            for (let i = 0; i < dayGroup.length; i++) {
              const dayChar = dayGroup[i];
              
              // Map day character to day of week number
              const dayMap = {
                'M': 1, // Monday
                'T': 2, // Tuesday
                'W': 3, // Wednesday
                'R': 4, // Thursday
                'F': 5  // Friday
              };
              
              if (!dayMap[dayChar]) {
                console.warn(`Invalid day character: "${dayChar}" for course ${course.Course}`);
                continue;
              }
              
              // Get this week's date for the day of the week
              const dayOfWeek = dayMap[dayChar];
              const today = moment().startOf('week');
              const courseDate = today.day(dayOfWeek);
              
              // Parse time strings to create Date objects
              const start = parseTimeString(courseDate.clone(), timePair.start);
              const end = parseTimeString(courseDate.clone(), timePair.end);
              
              if (!start || !end) {
                console.error(`Invalid time range for course ${course.Course} on day ${dayChar}: ${timePair.start}-${timePair.end}`);
                continue;
              }
              
              console.log(`Created event for ${course.Course} on ${dayChar}:`, {
                start: start.format('ddd h:mm A'),
                end: end.format('ddd h:mm A'),
                location: location
              });
              
              // Add event for this class session
              events.push({
                id: `${course.CRN || 'preview'}-${dayChar}-special-${isPreview ? 'preview' : 'regular'}`,
                title: `${course.Course}: ${course.Title}`,
                start: start.toDate(),
                end: end.toDate(),
                course: course,
                isPreview: isPreview,
                location: location,
                instructor: course.Instructor || 'TBA'
              });
            }
          });
          
          // We've handled this special case, so return to avoid further processing
          return events;
        }
      }
      
      // If no structured schedule, fall back to the old logic
      // Handle different day formats
      let courseDayStr = '';
      
      if (typeof course.Days === 'string') {
        courseDayStr = course.Days;
      } else if (Array.isArray(course.Days)) {
        courseDayStr = course.Days.join('');
      } else if (course.Days) {
        courseDayStr = String(course.Days);
      }
      
      console.log(`Course day string: "${courseDayStr}"`);
      
      // If days are formatted as "TR" meaning Tuesday/Thursday, treat each character as a day code
      const dayMap = {
        'M': 1, // Monday
        'T': 2, // Tuesday
        'W': 3, // Wednesday
        'R': 4, // Thursday
        'F': 5  // Friday
      };
      
      // Get all day characters (e.g., 'MWF' -> ['M','W','F'])
      const dayChars = courseDayStr.split('');
      console.log(`Day characters for ${courseDayStr}:`, dayChars);
      
      if (dayChars.length === 0) {
        console.warn(`No valid day characters found for course ${course.Course}`);
        return events;
      }
      
      // Get locations (buildings) as array
      let locations = [];
      if (typeof course.Location === 'string') {
        locations = [course.Location];
      } else if (Array.isArray(course.Location)) {
        locations = course.Location;
      }
      
      // Extract all time pairs
      const timePairs = [];
      
      if (typeof course.Times === 'string') {
        if (course.Times.includes('-')) {
          const [start, end] = course.Times.split('-').map(t => t.trim());
          timePairs.push({ start, end });
        }
      } else if (Array.isArray(course.Times)) {
        if (course.Times.length === 2 && typeof course.Times[0] === 'string' && typeof course.Times[1] === 'string') {
          // Format: ["0800", "0915"]
          timePairs.push({ start: course.Times[0], end: course.Times[1] });
        } else {
          // Try to extract multiple time pairs
          course.Times.forEach(timeItem => {
            if (typeof timeItem === 'string' && timeItem.includes('-')) {
              const [start, end] = timeItem.split('-').map(t => t.trim());
              timePairs.push({ start, end });
            } else if (Array.isArray(timeItem) && timeItem.length === 2) {
              timePairs.push({ start: timeItem[0], end: timeItem[1] });
            }
          });
        }
      }
      
      // Make sure we have at least one time pair
      if (timePairs.length === 0) {
        console.warn(`No valid time pairs found for course ${course.Course}`);
        timePairs.push({ start: null, end: null });
      }
      
      dayChars.forEach((dayChar, dayIndex) => {
        if (!dayMap[dayChar]) {
          console.warn(`Invalid day character: "${dayChar}" for course ${course.Course}`);
          return;
        }
        
        // Get this week's date for the day of the week
        const dayOfWeek = dayMap[dayChar];
        const today = moment().startOf('week');
        const courseDate = today.day(dayOfWeek);
        
        // Get corresponding time pair and location
        const timePairIndex = Math.min(dayIndex, timePairs.length - 1);
        const { start: startTime, end: endTime } = timePairs[timePairIndex];
        
        // Get the corresponding location for this time slot
        const location = locations[timePairIndex] || locations[0] || 'TBA';
        
        // Parse time strings to create Date objects
        const start = parseTimeString(courseDate.clone(), startTime);
        const end = parseTimeString(courseDate.clone(), endTime);
        
        if (!start || !end) {
          console.error(`Invalid time range for course ${course.Course} on day ${dayChar}: ${startTime}-${endTime}`);
          return;
        }
        
        console.log(`Created event for ${course.Course} on ${dayChar}:`, {
          start: start.format('ddd h:mm A'),
          end: end.format('ddd h:mm A'),
          location: location
        });
        
        // Add event for this class session
        events.push({
          id: `${course.CRN || 'preview'}-${dayChar}-${isPreview ? 'preview' : 'regular'}`,
          title: `${course.Course}: ${course.Title}`,
          start: start.toDate(),
          end: end.toDate(),
          course: course,
          isPreview: isPreview,
          location: location,
          instructor: course.Instructor || 'TBA'
        });
      });
    } catch (error) {
      console.error("Error creating calendar event for course:", course, error);
    }
    
    return events;
  };

  // Generate event objects for Big Calendar
  const calendarEvents = useMemo(() => {
    console.log("GENERATING CALENDAR EVENTS FROM COURSES:", courses);
    
    if (!courses || courses.length === 0) {
      console.log("No courses available to create events");
      return [];
    }
    
    let events = [];
    
    courses.forEach((course) => {
      events = [...events, ...createEventsForCourse(course)];
    });
    
    console.log(`Generated ${events.length} calendar events from ${courses.length} courses:`, events);
    return events;
  }, [courses]);
  
  // Generate preview events for the hovered course
  const previewEvents = useMemo(() => {
    if (!hoveredCourse) return [];
    
    // Create preview events for the hovered course
    return createEventsForCourse(hoveredCourse, true);
  }, [hoveredCourse]);
  
  // Combine regular events and preview events
  const allEvents = useMemo(() => {
    return [...calendarEvents, ...previewEvents];
  }, [calendarEvents, previewEvents]);

  // Generate a consistent color based on course department
  const getCourseColor = (course) => {
    if (!course || !course.Course) return `hsl(210, 100%, 97%)`;
    
    // Extract department code from course
    const deptMatch = course.Course.toString().match(/^([A-Z]+)/);
    const dept = deptMatch ? deptMatch[0] : '';
    
    // Predefined colors for common departments (using Clark University colors where possible)
    const departmentColors = {
      "CSCI": "#e6f7ff", // Computer Science - Light blue
      "MATH": "#fff0f6", // Mathematics - Light pink
      "PHYS": "#f6ffed", // Physics - Light green
      "CHEM": "#fffbe6", // Chemistry - Light yellow
      "BIOL": "#e6fffb", // Biology - Light teal
      "HIST": "#f9f0ff", // History - Light purple
      "ECON": "#fff7e6", // Economics - Light orange
      "PSYC": "#fcf5e9", // Psychology - Light peach
      "ENG": "#f5f5f5",  // English - Light gray
      "POLS": "#e8f4ff", // Political Science - Light sky blue
      "GEOG": "#e6fff2", // Geography - Light mint
      "SOC": "#ffefd6",  // Sociology - Light tan
      "ACCT": "#f0f9ff", // Accounting - Light azure
      "MGMT": "#f5faff", // Management - Light steel blue
      "ARTS": "#fff1f0", // Arts - Light salmon
      "PHIL": "#f6e6ff", // Philosophy - Light lavender
      "SPAN": "#fdf9e8", // Spanish - Light khaki
      "FREN": "#e7ecff", // French - Light periwinkle
      "GERM": "#fce9e9"  // German - Light rose
    };
    
    // Use predefined color if available
    if (departmentColors[dept]) {
      return departmentColors[dept];
    }
    
    // Otherwise generate a color based on department name hash
    let hash = 0;
    for (let i = 0; i < dept.length; i++) {
      hash = dept.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Generate a pastel color
    const h = hash % 360;
    const s = 35 + (hash % 30); // 35-65% saturation
    const l = 92 + (hash % 5);  // 92-97% lightness for pastel
    
    return `hsl(${h}, ${s}%, ${l}%)`; // Pastel shade for better text contrast
  };

  // Format the time periods for display
  const formatCourseTimes = (course) => {
    if (!course || !course.Times) return "TBA";
    
    const times = Array.isArray(course.Times) ? course.Times : [course.Times];
    
    return times.map(time => {
      if (Array.isArray(time) && time.length === 2) {
        return `${formatTimeDisplay(time[0])} - ${formatTimeDisplay(time[1])}`;
      } else if (typeof time === 'string' && time.includes('-')) {
        const [start, end] = time.split('-').map(t => t.trim());
        return `${formatTimeDisplay(start)} - ${formatTimeDisplay(end)}`;
      }
      return "TBA";
    }).join(" | ");
  };

  // Format time for display
  const formatTimeDisplay = (timeStr) => {
    if (!timeStr) return '';
    
    // If it's in military format (4 digits without colon)
    if (timeStr.length === 4 && !timeStr.includes(':')) {
      const hours = parseInt(timeStr.substring(0, 2));
      const minutes = timeStr.substring(2, 4);
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 === 0 ? 12 : hours % 12;
      return `${displayHours}:${minutes} ${period}`;
    }
    
    return timeStr; // Return as is if already formatted
  };

  // Format the course days for display
  const formatCourseDays = (course) => {
    if (!course || !course.Days) return "TBA";
    
    const days = Array.isArray(course.Days) ? course.Days : [course.Days];
    return days.join(", ");
  };
  
  // Custom event component for Big Calendar
  const EventComponent = ({ event }) => {
    const course = event.course;
    
    // Calculate duration in minutes
    const durationMinutes = (event.end - event.start) / (1000 * 60);
    const isShortClass = durationMinutes <= 50;
    
    return (
      <div className="rbc-event-content">
        {isShortClass ? (
          <p className="rbc-event-label">{course.Course} - {event.location || 'TBA'}</p>
        ) : (
          <>
            <p className="rbc-event-label">{course.Course}</p>
            <p className="rbc-event-location">{event.location || 'TBA'}</p>
          </>
        )}
      </div>
    );
  };

  // Toggle debug info
  const toggleDebug = () => {
    setDebug(!debug);
  };

  // Store debug data for rendering
  const [debugData, setDebugData] = useState({
    parseErrors: [],
    timeResults: [],
    lastError: ''
  });

  // Add more debug data collection
  useEffect(() => {
    if (debug) {
      const parseResults = [];
      const errors = [];
      
      // Try to parse each course's times for debugging
      courses.forEach(course => {
        try {
          if (!course.Times) {
            errors.push(`Course ${course.Course || 'Unknown'} has no Times data`);
            return;
          }
          
          let timeValue = '';
          if (typeof course.Times === 'string') {
            timeValue = course.Times;
          } else if (Array.isArray(course.Times) && course.Times.length > 0) {
            timeValue = course.Times[0];
          }
          
          // Try parsing a sample time
          if (typeof timeValue === 'string') {
            // Get a sample date
            const sampleDate = moment().startOf('week').day(1); // Monday
            
            if (timeValue.includes('-')) {
              const [startStr] = timeValue.split('-');
              const parsedTime = parseTimeString(sampleDate, startStr.trim());
              if (parsedTime) {
                parseResults.push(`Successfully parsed "${startStr}" → ${parsedTime.format('h:mm A')}`);
              } else {
                errors.push(`Failed to parse "${startStr}" from ${timeValue}`);
              }
            } else {
              // Try direct parsing
              const parsedTime = parseTimeString(sampleDate, timeValue);
              if (parsedTime) {
                parseResults.push(`Successfully parsed "${timeValue}" → ${parsedTime.format('h:mm A')}`);
              } else {
                errors.push(`Failed to parse "${timeValue}"`);
              }
            }
          } else {
            errors.push(`Unusual time format: ${typeof timeValue}, value: ${JSON.stringify(timeValue)}`);
          }
        } catch (e) {
          errors.push(`Error analyzing course ${course.Course}: ${e.message}`);
        }
      });
      
      setDebugData({
        parseErrors: errors,
        timeResults: parseResults,
        lastError: debugData.lastError
      });
    }
  }, [courses, debug]);

  // Handle double click on an event
  const handleEventDoubleClick = (event, e) => {
    e.preventDefault(); // Prevent default action
    
    // Set context menu position and associated course
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      course: event.course
    });
  };
  
  // Close the context menu
  const closeContextMenu = () => {
    setContextMenu(null);
  };
  
  // Handle document click to close context menu
  useEffect(() => {
    const handleDocumentClick = () => {
      closeContextMenu();
    };
    
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  // Handle viewport resize
  useEffect(() => {
    const handleResize = () => {
      setDropdownOpen(false); // Close dropdown when viewport changes
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Function to toggle the dropdown menu
  const toggleDropdown = (e) => {
    e.stopPropagation(); // Prevent event from bubbling up
    setDropdownOpen(!dropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (dropdownOpen) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Function to check if a date falls on a no-class day
  const isNoClassDay = (date) => {
    // No need to check if we're not excluding breaks
    if (!excludeBreaks) return false;
    
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    
    // Create a formatted date string for easier comparison (YYYY-MM-DD)
    const formattedDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    // List of no-class days for Fall 2025
    const noClassDays = [
      // Labor Day
      '2025-09-01',
      
      // Fall Break
      '2025-10-13', '2025-10-14',
      
      // Thanksgiving break
      '2025-11-26', '2025-11-27', '2025-11-28',
      
      // Reading Days and Final Exams
      '2025-12-09', '2025-12-10', '2025-12-11', '2025-12-12', 
      '2025-12-13', '2025-12-14', '2025-12-15', '2025-12-16', '2025-12-17'
    ];
    
    return noClassDays.includes(formattedDate);
  };

  // Function to export calendar as ICS file
  const exportCalendar = () => {
    if (calendarEvents.length === 0) {
      alert('No courses to export. Please add some courses first.');
      return;
    }

    console.log("Exporting calendar using calendar events:", calendarEvents);

    // Set the semester dates for Fall 2025 (for RRULE end date)
    const semesterEnd = new Date(2025, 11, 8); // December 8, 2025

    // Get timezone information
    const tzid = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
    const date = new Date();
    const tzOffset = -date.getTimezoneOffset();
    const offsetHours = Math.abs(Math.floor(tzOffset / 60));
    const offsetMinutes = Math.abs(tzOffset % 60);
    const offsetSign = tzOffset >= 0 ? '+' : '-';
    const offsetFormatted = `${offsetSign}${String(offsetHours).padStart(2, '0')}${String(offsetMinutes).padStart(2, '0')}`;

    // Create ICS calendar header
    let icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Clark Course Scheduler//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VTIMEZONE',
      `TZID:${tzid}`,
      'BEGIN:STANDARD',
      'DTSTART:20000101T000000',
      `TZOFFSETFROM:${offsetFormatted}`,
      `TZOFFSETTO:${offsetFormatted}`,
      `TZNAME:${tzid}`,
      'END:STANDARD',
      'END:VTIMEZONE'
    ];

    // Group events by course to avoid duplicates
    const courseEvents = {};
    
    // Process each calendar event
    calendarEvents.forEach(event => {
      const course = event.course;
      const courseKey = `${course.Course}-${course.CRN || 'unknown'}`;
      
      if (!courseEvents[courseKey]) {
        courseEvents[courseKey] = [];
      }
      
      courseEvents[courseKey].push(event);
    });
    
    // Process each unique course's events
    Object.keys(courseEvents).forEach(courseKey => {
      const events = courseEvents[courseKey];
      
      // Process each event for this course
      events.forEach(event => {
        // Get day of week from the event date
        const dayOfWeek = event.start.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const icsDay = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'][dayOfWeek];
        
        if (!icsDay) return; // Skip if we can't determine the day
        
        const course = event.course;
        
        // Format local time without timezone conversion
        const formatTime = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          
          return `${year}${month}${day}T${hours}${minutes}${seconds}`;
        };
        
        // Format UTC time for UNTIL property in RRULE
        const formatUTCTime = (date) => {
          const utcDate = new Date(Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            23, 59, 59 // End of day
          ));
          
          const year = utcDate.getUTCFullYear();
          const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
          const day = String(utcDate.getUTCDate()).padStart(2, '0');
          const hours = String(utcDate.getUTCHours()).padStart(2, '0');
          const minutes = String(utcDate.getUTCMinutes()).padStart(2, '0');
          const seconds = String(utcDate.getUTCSeconds()).padStart(2, '0');
          
          return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
        };
        
        // Create a unique ID for the event
        const uid = `${course.CRN || Math.random().toString(36).substring(2, 10)}-${icsDay}-${event.start.getHours()}${event.start.getMinutes()}@clarkcourses`;
        
        // Format description
        const description = (course.Description || `${course.Course}: ${course.Title}`)
          .replace(/\n/g, '\\n')  // Replace newlines with escaped newlines
          .replace(/,/g, '\\,')   // Escape commas
          .replace(/;/g, '\\;');  // Escape semicolons
        
        // Log the event times for debugging
        console.log(`Adding event for ${course.Course} on ${icsDay}:`, {
          start: event.start.toLocaleTimeString(),
          end: event.end.toLocaleTimeString(),
          uid: uid
        });
        
        // Add the event
        icsContent.push('BEGIN:VEVENT');
        icsContent.push(`UID:${uid}`);
        icsContent.push(`SUMMARY:${course.Course}: ${course.Title}`);
        icsContent.push(`DESCRIPTION:${description}`);
        icsContent.push(`LOCATION:${event.location || 'TBA'}`);
        icsContent.push(`DTSTART;TZID=${tzid}:${formatTime(event.start)}`);
        icsContent.push(`DTEND;TZID=${tzid}:${formatTime(event.end)}`);
        icsContent.push(`DTSTAMP:${formatUTCTime(new Date())}`);
        icsContent.push(`RRULE:FREQ=WEEKLY;UNTIL=${formatUTCTime(semesterEnd)};BYDAY=${icsDay}`);
        
        // Add no-class days as exceptions if excludeBreaks is enabled
        if (excludeBreaks) {
          // Generate EXDATE for this day of the week that fall on no-class days
          const exdates = generateExceptionDates(dayOfWeek, event.start);
          if (exdates.length > 0) {
            icsContent.push(`EXDATE;TZID=${tzid}:${exdates.join(',')}`);
          }
        }
        
        icsContent.push('STATUS:CONFIRMED');
        icsContent.push(`ORGANIZER;CN=${course.Instructor || 'TBA'}:mailto:instructor@example.com`);
        icsContent.push('END:VEVENT');
      });
    });
    
    // Finish the ICS file
    icsContent.push('END:VCALENDAR');
    
    // Join all lines with CRLF as required by iCalendar spec
    const icsData = icsContent.join('\r\n');
    
    // Create the download link
    const blob = new Blob([icsData], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Clark_Schedule${excludeBreaks ? '_NoBreaks' : ''}.ics`);
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  // Helper function to generate exception dates for no-class days
  const generateExceptionDates = (dayOfWeek, referenceDate) => {
    const exdates = [];
    
    // No need to continue if we're not excluding breaks
    if (!excludeBreaks) return exdates;
    
    // Calculate the date of the first occurrence of this day in the semester
    const semesterStart = new Date(2025, 7, 25); // August 25, 2025
    const semesterEnd = new Date(2025, 11, 8);   // December 8, 2025
    
    // Find the first occurrence of this day after semester start
    let currentDate = new Date(semesterStart);
    while (currentDate.getDay() !== dayOfWeek) {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // Preserve the time from the reference date
    const hours = referenceDate.getHours();
    const minutes = referenceDate.getMinutes();
    const seconds = referenceDate.getSeconds();
    
    // Loop through all occurrences of this day until the end of the semester
    while (currentDate <= semesterEnd) {
      if (isNoClassDay(currentDate)) {
        // Create a copy of the date with the same time
        const exDate = new Date(currentDate);
        exDate.setHours(hours, minutes, seconds);
        
        // Format the date for ICS EXDATE (YYYYMMDDThhmmss)
        const year = exDate.getFullYear();
        const month = String(exDate.getMonth() + 1).padStart(2, '0');
        const day = String(exDate.getDate()).padStart(2, '0');
        const formattedHours = String(hours).padStart(2, '0');
        const formattedMinutes = String(minutes).padStart(2, '0');
        const formattedSeconds = String(seconds).padStart(2, '0');
        
        exdates.push(`${year}${month}${day}T${formattedHours}${formattedMinutes}${formattedSeconds}`);
      }
      
      // Move to the next week
      currentDate.setDate(currentDate.getDate() + 7);
    }
    
    return exdates;
  };

  // New function to export directly to Google Calendar
  const exportToGoogleCalendar = () => {
    if (courses.length === 0) {
      alert('No courses to export. Please add courses to your schedule first.');
      return;
    }

    // Show the Google Calendar instructions modal
    setShowGoogleModal(true);
    
    // Call the exportCalendar function to create the ICS file
    exportCalendar();
  };

  // Function to close the Google Calendar modal
  const closeGoogleModal = () => {
    setShowGoogleModal(false);
  };

  const handleClearSchedule = () => {
    if (courses.length === 0) return;

    if (window.confirm('Clear your saved schedule from this browser?')) {
      onClearSchedule();
      setDropdownOpen(false);
      setContextMenu(null);
    }
  };

  // Toggle compact view mode
  const toggleCompactView = () => {
    // Simply toggle the state without DOM manipulation
    setCompactView(!compactView);
  };

  // Function to handle file input change for importing ICS
  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportStatus('Reading file...');
    
    try {
      const fileContent = await readFileAsText(file);
      const courseCodes = extractCourseCodesFromICS(fileContent);
      
      if (courseCodes.length === 0) {
        setImportStatus('No course codes found in the file.');
        return;
      }
      
      setImportStatus(`Found ${courseCodes.length} courses. Looking for matches...`);
      
      // Pass these course codes to the parent component to search and add
      if (typeof window.findAndAddCoursesByCodes === 'function') {
        window.findAndAddCoursesByCodes(courseCodes);
        setImportStatus(`Successfully requested ${courseCodes.length} courses to be added.`);
      } else {
        // Fallback if the global function isn't available
        setImportStatus('Found courses: ' + courseCodes.join(', '));
      }
      
      // Reset the file input
      if (importInputRef.current) {
        importInputRef.current.value = '';
      }
      
      // Close modal after a delay
      setTimeout(() => {
        setShowImportModal(false);
        setImportStatus('');
      }, 3000);
      
    } catch (error) {
      console.error('Error importing file:', error);
      setImportStatus(`Error: ${error.message || 'Could not process the file.'}`);
    }
  };
  
  // Helper function to read file as text
  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };
  
  // Function to extract just the course codes from ICS file
  const extractCourseCodesFromICS = (icsContent) => {
    console.log("Extracting course codes from ICS content...");
    const courseCodes = new Set(); // Use Set to avoid duplicates
    
    // Split into lines and find all events
    const lines = icsContent.split(/\r\n|\n|\r/);
    
    // Look for SUMMARY lines which contain course information
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Check if this is a summary line with course info
      if (line.startsWith('SUMMARY:')) {
        const summary = line.substring(8); // Remove "SUMMARY:" prefix
        
        // Extract course code (e.g., "CSCI 120") from summary
        // Pattern matches department code + course number 
        const courseCodeMatch = summary.match(/^([A-Z]+\s+\d+)/);
        
        if (courseCodeMatch) {
          const courseCode = courseCodeMatch[1];
          courseCodes.add(courseCode);
          console.log(`Found course code: ${courseCode}`);
        }
      }
    }
    
    return Array.from(courseCodes);
  };
  
  // Function to show import modal
  const showImportSchedule = () => {
    setImportStatus('');
    setShowImportModal(true);
  };
  
  // Function to close import modal
  const closeImportModal = () => {
    setShowImportModal(false);
    setImportStatus('');
  };

  // Add this function to create a simple ICS file with just course summaries
  const createSampleICSwithCourseNames = () => {
    // Create a simplified ICS file that just includes the course names
    const header = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Clark Sample Courses//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH'
    ];
    
    const events = courses.map(course => {
      // Create a simple event with just the course name
      return [
        'BEGIN:VEVENT',
        `UID:sample-${course.CRN || Math.random().toString(36).substring(2, 10)}@clarkcourses`,
        `SUMMARY:${course.Course}: ${course.Title}`,
        'DTSTART:20250901T090000',
        'DTEND:20250901T103000',
        'END:VEVENT'
      ].join('\r\n');
    });
    
    const footer = ['END:VCALENDAR'];
    
    // Combine all parts
    return [...header, ...events, ...footer].join('\r\n');
  };

  return (
    <div className={`schedule-container ${compactView ? 'compact-mode' : ''}`}>
      <div className="schedule-header">
        <h2>Schedule</h2>
        <div className="schedule-tools">
          <div className="view-toggle">
            <button 
              className={`view-button ${view === 'calendar' ? 'active' : ''}`}
              onClick={() => setView('calendar')}
            >
              Calendar View
            </button>
            <button 
              className={`view-button ${view === 'list' ? 'active' : ''}`}
              onClick={() => setView('list')}
            >
              List View
            </button>
          </div>
          
          {view === 'calendar' && (
            <div className="extra-tools">
              <div className="export-dropdown">
                <button className="export-button" onClick={toggleDropdown}>
                  Export Calendar ▼
                </button>
                {dropdownOpen && (
                  <div className="dropdown-menu">
                    <div className="dropdown-item" onClick={() => {
                      exportToGoogleCalendar();
                      setDropdownOpen(false);
                    }}>
                      Export to Google Calendar
                    </div>
                    <div className="dropdown-item" onClick={() => {
                      exportCalendar();
                      setDropdownOpen(false);
                    }}>
                      Export as ICS File
                    </div>
                    <div className="dropdown-divider"></div>
                    <div className="dropdown-item" onClick={() => {
                      onDownloadSchedule();
                      setDropdownOpen(false);
                    }}>
                      Download Schedule
                    </div>
                    <div className="dropdown-item">
                      <label className="upload-label" style={{ cursor: 'pointer', display: 'block', width: '100%' }}>
                        Upload Schedule
                        <input 
                          type="file" 
                          accept=".json"
                          onChange={onUploadSchedule}
                          onClick={(e) => e.stopPropagation()}
                          style={{ display: 'none' }}
                        />
                      </label>
                    </div>
                    <div className="dropdown-divider"></div>
                    <div className="dropdown-item checkbox-item" onClick={(e) => {
                      e.stopPropagation(); // Prevent dropdown from closing
                      setExcludeBreaks(!excludeBreaks);
                    }}>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={excludeBreaks}
                          onChange={() => setExcludeBreaks(!excludeBreaks)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        Exclude breaks & holidays
                      </label>
                    </div>
                  </div>
                )}
              </div>
              <div className="button-container">
                <button className="fit-screen-button" onClick={toggleCompactView}>
                  {compactView ? 'Standard View' : 'Fit to Screen'}
                </button>
              </div>
              {/* Debug button commented out
              <button className="debug-button" onClick={() => setShowDebug(!showDebug)}>
                {showDebug ? 'Hide Debug' : 'Show Debug'}
              </button>
              */}
            </div>
          )}
          <div className="button-container">
            <button
              className="clear-schedule-button"
              onClick={handleClearSchedule}
              disabled={courses.length === 0}
            >
              Clear Schedule
            </button>
          </div>
        </div>
      </div>

      <div className="schedule-content">
        {courses.length === 0 ? (
          <div className="empty-schedule">
            <h3>Your schedule is empty</h3>
            <p>Add courses from the course catalog to see them in your schedule.</p>
          </div>
        ) : view === 'calendar' ? (
          <div className={`calendar-container ${compactView ? 'compact-calendar' : ''}`}>
            <Calendar
              localizer={localizer}
              events={allEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: compactView ? 'auto' : 500 }}
              components={{
                event: EventComponent,
                toolbar: () => null // This removes the toolbar with navigation buttons
              }}
              onDoubleClickEvent={handleEventDoubleClick}
              eventPropGetter={(event) => {
                const dept = event.course.Course?.split(' ')[0] || '';
                
                // Department-specific accent colors for the left border
                const deptAccentColors = {
                  "CSCI": "#1890ff", // Computer Science - Blue
                  "MATH": "#eb2f96", // Mathematics - Pink
                  "PHYS": "#52c41a", // Physics - Green
                  "CHEM": "#faad14", // Chemistry - Yellow
                  "BIOL": "#13c2c2", // Biology - Teal
                  "HIST": "#722ed1", // History - Purple
                  "ECON": "#fa8c16", // Economics - Orange
                  "PSYC": "#fa541c", // Psychology - Sunset
                  "ENG": "#8c8c8c",  // English - Gray
                  "POLS": "#1677ff", // Political Science - Blue
                  "GEOG": "#52c41a", // Geography - Green
                  "SOC": "#fa8c16",  // Sociology - Orange
                  "ACCT": "#0958d9", // Accounting - Dark blue
                  "MGMT": "#096dd9", // Management - Blue
                  "ARTS": "#f5222d", // Arts - Red
                  "PHIL": "#722ed1", // Philosophy - Purple
                  "SPAN": "#d4b106", // Spanish - Gold
                  "FREN": "#2f54eb", // French - Royal blue
                  "GERM": "#cf1322"  // German - Red
                };
                
                // Default accent color (Clark red)
                const accentColor = deptAccentColors[dept] || "#C8102E";
                const backgroundColor = getCourseColor(event.course);
                
                return {
                  style: {
                    backgroundColor: backgroundColor,
                    borderLeft: `4px solid ${accentColor}`,
                    borderStyle: event.isPreview ? 'dashed' : 'solid',
                    boxShadow: event.isPreview 
                      ? '0 0 0 2px rgba(0, 0, 0, 0.1)' 
                      : '0 2px 4px rgba(0, 0, 0, 0.1)',
                    borderRadius: '4px',
                    padding: compactView ? '2px' : '4px',
                    minHeight: compactView ? '18px' : '24px',
                    fontSize: compactView ? '0.75rem' : '0.85rem',
                    color: '#0C2340',
                    fontWeight: '500',
                    lineHeight: '1.2',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                };
              }}
              step={compactView ? 30 : 60}
              timeslots={compactView ? 2 : 1}
              min={new Date(2024, 0, 1, 8, 0, 0)}
              max={new Date(2024, 0, 1, 22, 0, 0)}
              formats={{
                timeGutterLabel: (date, culture, localizer) => {
                  return localizer.format(date, compactView ? 'h A' : 'h:mm A', culture);
                }
              }}
              defaultView="week"
              views={['week']}
              tooltipAccessor={(event) => `${event.course.Course} - ${event.course.Title}`}
            />
          </div>
        ) : (
          <div className="course-list-view">
            <h3>Selected Courses</h3>
            {courses.length === 0 ? (
              <p className="no-courses">No courses selected. Add courses from the catalog.</p>
            ) : (
              <div className="course-list">
                {courses.map(course => (
                  <div key={course.CRN} className="schedule-course-card">
                    <div className="course-info">
                      <h4>{course.Course}: {course.Title}</h4>
                      <p><strong>Professor:</strong> {course.Instructor || 'TBA'}</p>
                      <p><strong>Days:</strong> {formatCourseDays(course)}</p>
                      <p><strong>Times:</strong> {formatCourseTimes(course)}</p>
                    </div>
                    <button 
                      className="remove-button"
                      onClick={() => removeCourse(course)}
                      aria-label={`Remove ${course.Course} from schedule`}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {showDebug && (
        <div className="debug-info">
          <h3>Debug Information</h3>
          <p>Number of courses: {courses.length}</p>
          <p>Number of events: {allEvents.length}</p>
          <div className="courses-list">
            {courses.map((course, index) => (
              <div key={index} className="course-debug-item">
                <p><strong>Course:</strong> {course.Course}</p>
                <p><strong>Title:</strong> {course.Title}</p>
                <p><strong>Days:</strong> {Array.isArray(course.Days) ? course.Days.join(', ') : course.Days}</p>
                <p><strong>Times:</strong> {Array.isArray(course.Times) ? 
                  course.Times.map(time => Array.isArray(time) ? `${time[0]} - ${time[1]}` : time).join(' | ') : 
                  course.Times}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Google Calendar Instructions Modal */}
      {showGoogleModal && (
        <div className="modal-overlay" onClick={closeGoogleModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Import to Google Calendar</h3>
              <button className="modal-close" onClick={closeGoogleModal}>×</button>
            </div>
            <div className="modal-body">
              <p>To import your schedule into Google Calendar:</p>
              <ol>
                <li>Go to <a href="https://calendar.google.com/" target="_blank" rel="noopener noreferrer">Google Calendar</a></li>
                <li>Click the "+" button next to "Other calendars" in the sidebar</li>
                <li>Select "Import"</li>
                <li>Upload the ICS file you just downloaded</li>
                <li>Choose the calendar where you want to add the events</li>
                <li>Click "Import"</li>
              </ol>
              <div className="modal-tip">
                <strong>Tip:</strong> Create a new calendar specifically for your courses to keep them organized.
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Import Schedule Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={closeImportModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Import Schedule</h3>
              <button className="modal-close" onClick={closeImportModal}>×</button>
            </div>
            <div className="modal-body">
              <p>Upload an ICS file to import your previously saved schedule.</p>
              
              <div className="file-upload-container">
                <input 
                  type="file" 
                  accept=".ics" 
                  onChange={handleImportFile}
                  ref={importInputRef}
                  className="file-input"
                  id="schedule-import"
                />
                <label htmlFor="schedule-import" className="file-upload-button">
                  Select ICS File
                </label>
              </div>
              
              {importStatus && (
                <div className={`import-status ${importStatus.includes('Error') ? 'error' : ''}`}>
                  {importStatus}
                </div>
              )}
              
              <div className="modal-note">
                <strong>Note:</strong> This will add the courses from the ICS file to your current schedule.
                Only courses previously exported from this tool can be imported correctly.
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Context Menu */}
      {contextMenu && !contextMenu.course.isPreview && (
        <div 
          className="context-menu"
          style={{
            position: 'fixed',
            top: `${contextMenu.y}px`,
            left: `${contextMenu.x}px`,
            zIndex: 1000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="context-menu-item" onClick={() => {
            removeCourse(contextMenu.course);
            closeContextMenu();
          }}>
            Remove {contextMenu.course.Course}
          </div>
          <div className="context-menu-item" onClick={closeContextMenu}>
            Cancel
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedule; 
